import { AvailableCheckInDate, Maybe } from '@gql/schema';
import { formatMomentToString } from '@utils/dates.utils';
import {
  averagePricePerNight,
  getPriceWithBookingFee,
  numberToCurrency,
} from '@utils/pricing.utils';
import moment from 'moment';
import { FocusedInput } from '../AvailabilityPresentation.interfaces';
import { DayData, DayDataConfig, NullableMoment, VisibleMonths } from './Calendar.interfaces';

/*
Notes on pricing:
  HO:
    - If dates unselected, price shown is check in price for night, reflective of number of rooms selected.
    - If mlos is more than one night, price shown will be average per night.
    - If dates selected, prices shown in selected range will be average per night.

  H+:
    - Price shown will be total cost of stay if check in on that date.

  Tracy returns total price of stay
*/
const determinePrice = (config: DayDataConfig, price: Maybe<number>, nights = 1) => {
  if (!price) {
    return '';
  }

  if (config.withFlights) {
    return numberToCurrency(price, config.currency, config.territory, true);
  }

  const numOfRooms = config.rooms ?? 1;
  const basePrice = price * numOfRooms;

  const finalPrice = config.applyBookingFees ? getPriceWithBookingFee(basePrice) : basePrice;

  return averagePricePerNight(finalPrice, nights, config);
};

export const getCheckInEntry = (
  day: NullableMoment,
  availableCheckInDates: Maybe<AvailableCheckInDate[]>
) => {
  const formattedDay = formatMomentToString(day);

  return availableCheckInDates?.find(entry => entry?.date === formattedDay);
};

export const getCheckOutEntry = (
  startDate: NullableMoment,
  endDate: NullableMoment,
  availableCheckInDates: Maybe<AvailableCheckInDate[]>
) => {
  if (!availableCheckInDates) {
    return undefined;
  }

  const formattedEndDate = formatMomentToString(endDate);
  const { checkOutDates } = getCheckInEntry(startDate, availableCheckInDates) ?? {};

  return formattedEndDate
    ? checkOutDates?.find(entry => entry?.date === formattedEndDate)
    : checkOutDates?.[0];
};

export const getDayData = (
  day: moment.Moment,
  startDate: NullableMoment,
  endDate: NullableMoment,
  availableCheckInDates: Maybe<AvailableCheckInDate[]>,
  config: DayDataConfig
): DayData => {
  const formattedDay = formatMomentToString(day);

  const commonDayData = {
    day,
    formattedDay,
    isAvailable: false,
    price: '',
    isFirstCheckOut: false,
    isUnavailableCheckOut: false,
    inSelectedRange: false,
  };

  if (!availableCheckInDates) {
    return commonDayData;
  }

  const isCheckInInputFocused = config.focusedInput === FocusedInput.StartDate;
  const isSelectedCheckIn = day.isSame(startDate, 'd');
  const isSelectedCheckOut = day.isSame(endDate, 'd');
  const isInSelectedRange = day.isBetween(startDate, endDate, 'd');
  const isBeforeSelectedCheckIn = day.isBefore(startDate, 'd');
  const isAfterSelectedCheckOut = day.isAfter(endDate, 'd');

  const checkInEntry = getCheckInEntry(day, availableCheckInDates);
  const canCheckIn = !!checkInEntry;
  const checkInEntryPrice = checkInEntry?.amount;

  // Bugfix DEV-81003: The cheapest rate isn't necessarily related to the stay of minimum length
  const cheapestCheckOutDateEntry = checkInEntry?.checkOutDates?.find(
    c => c?.amount === checkInEntryPrice
  );
  const durationOfStay = moment(cheapestCheckOutDateEntry?.date)
    .startOf('d')
    .diff(day.startOf('d'), 'd');

  const noDatesSelected = !startDate && !endDate;

  if (noDatesSelected) {
    return {
      ...commonDayData,
      isAvailable: canCheckIn,
      price: determinePrice(config, checkInEntryPrice, durationOfStay),
    };
  }

  const selectedCheckInEntry = getCheckInEntry(startDate, availableCheckInDates);
  const canCheckOut = !!getCheckOutEntry(startDate, day, availableCheckInDates);

  const { checkOutDates } = selectedCheckInEntry ?? {};
  const lastCheckOutEntry = checkOutDates?.[checkOutDates.length - 1];
  const firstCheckOutDate = checkOutDates?.[0]?.date;
  const lastAvailableCheckOutDate = lastCheckOutEntry ? moment(lastCheckOutEntry.date) : null;

  const isPossibleCheckOut = day.isBetween(startDate, lastAvailableCheckOutDate, 'd', '[]');
  const isMlosNights = day.isBetween(startDate, moment(firstCheckOutDate));

  if (isSelectedCheckOut) {
    return {
      ...commonDayData,
      isAvailable: canCheckOut,
      isFirstCheckOut: firstCheckOutDate === formattedDay && (checkOutDates?.length ?? 0) > 1,
    };
  }

  if (isPossibleCheckOut) {
    if (isCheckInInputFocused && (isSelectedCheckIn || isInSelectedRange)) {
      const earliestCheckOutEntry = checkInEntry?.checkOutDates?.[0];
      const earliestCheckOutEntryDate = moment(earliestCheckOutEntry?.date);
      const isSelectedEndDateBeforeMlos = earliestCheckOutEntryDate.isAfter(endDate);
      const endDayCheckOut = getCheckOutEntry(
        day,
        isSelectedEndDateBeforeMlos ? earliestCheckOutEntryDate : endDate,
        availableCheckInDates
      );
      const duration = moment(endDayCheckOut?.date)?.startOf('d').diff(day?.startOf('d'), 'd');
      const totalPrice = endDayCheckOut?.amount ?? 0;

      return {
        ...commonDayData,
        isAvailable: canCheckIn,
        price: determinePrice(config, totalPrice, duration),
        isUnavailableCheckOut: isMlosNights && !isCheckInInputFocused,
        inSelectedRange: !isSelectedCheckIn,
      };
    }

    if (!isCheckInInputFocused && (isSelectedCheckIn || isInSelectedRange)) {
      const endDayCheckOut = getCheckOutEntry(startDate, endDate, availableCheckInDates);
      const duration = moment(endDayCheckOut?.date)
        ?.startOf('d')
        .diff(startDate?.startOf('d'), 'd');
      const totalPrice = endDayCheckOut?.amount ?? 0;

      return {
        ...commonDayData,
        isAvailable: canCheckOut || (isSelectedCheckIn && canCheckIn),
        price: determinePrice(config, totalPrice, duration),
        isUnavailableCheckOut: isMlosNights,
        inSelectedRange: !isSelectedCheckIn,
      };
    }

    if (isCheckInInputFocused && !checkInEntryPrice) {
      return {
        ...commonDayData,
        isUnavailableCheckOut: !canCheckOut,
        inSelectedRange: isInSelectedRange,
      };
    }

    const checkOutPrice = getCheckOutEntry(startDate, day, availableCheckInDates)?.amount;
    const duration = isCheckInInputFocused
      ? durationOfStay
      : day?.startOf('d').diff(startDate?.startOf('d'), 'd');

    const priceToDisplay = isCheckInInputFocused ? checkInEntryPrice : checkOutPrice;

    return {
      ...commonDayData,
      isAvailable: isCheckInInputFocused || canCheckOut,
      price: determinePrice(config, priceToDisplay, duration),
      isUnavailableCheckOut: !isCheckInInputFocused && !canCheckOut,
      inSelectedRange: isInSelectedRange,
    };
  }

  if (isBeforeSelectedCheckIn) {
    return {
      ...commonDayData,
      isAvailable: canCheckIn,
      price: determinePrice(config, checkInEntryPrice, durationOfStay),
    };
  }

  if (isAfterSelectedCheckOut) {
    return {
      ...commonDayData,
      isAvailable: canCheckIn && isCheckInInputFocused,
      price: isCheckInInputFocused ? determinePrice(config, checkInEntryPrice, durationOfStay) : '',
    };
  }

  return commonDayData;
};

export const getVisibleMonths = (currentVisibleMonths: VisibleMonths, month: moment.Moment) => {
  if (!currentVisibleMonths.first) {
    return { first: month };
  }

  if (!currentVisibleMonths.last) {
    return { ...currentVisibleMonths, last: month };
  }

  if (month.isAfter(currentVisibleMonths.last, 'month')) {
    return { first: currentVisibleMonths.last, last: month };
  }

  if (month.isBefore(currentVisibleMonths.first, 'month')) {
    return { first: month, last: currentVisibleMonths.first };
  }

  return { ...currentVisibleMonths };
};

export const getAdditionalCalendarProps = (
  isMobile: boolean,
  firstDatesToShow: moment.Moment,
  lastDatesToShow: moment.Moment,
  locale: string
) => {
  if (!isMobile) {
    return {
      key: undefined,
      initialVisibleMonth: undefined,
      numberOfMonths: undefined,
    };
  }

  return {
    key: `${formatMomentToString(firstDatesToShow)}|${formatMomentToString(lastDatesToShow)}`,
    initialVisibleMonth: () => moment(firstDatesToShow).locale(locale),
    numberOfMonths:
      moment(lastDatesToShow)
        .startOf('month')
        .diff(moment(firstDatesToShow).startOf('month'), 'month') + 1,
  };
};

export const navigateNextMonth = () => {
  const nextButton: HTMLElement | null = document.querySelector("span[data-action='next-month']");
  nextButton?.click();
};
